import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react'
import { useAppDispatch } from '@/store'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { updateGroup } from '@/store/slices/groupsSlice'
import { Group } from '@/types'

interface Props {
  isOpen: boolean
  onClose: () => void
  group: Group
}

export default function EditGroupModal({ isOpen, onClose, group }: Props) {
  const { t } = useAppTranslation(['admin', 'common'])
  const dispatch = useAppDispatch()
  const toast = useToast()
  const [name, setName] = useState(group.name)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setName(group.name)
  }, [group.name])

  const handleUpdate = async () => {
    if (!name.trim()) return
    setIsLoading(true)
    try {
      await dispatch(updateGroup({ id: group.id, name: name.trim() })).unwrap()
      toast({
        title: t('groups.updateSuccess', { ns: 'admin' }),
        status: 'success',
        duration: 2000,
      })
      onClose()
    } catch {
      toast({ title: t('groups.updateFailed', { ns: 'admin' }), status: 'error', duration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('groups.editGroup', { ns: 'admin' })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>{t('groups.groupName', { ns: 'admin' })}</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUpdate()
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button colorScheme="blue" onClick={handleUpdate} isLoading={isLoading}>
            {t('groups.editGroup', { ns: 'admin' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
